import { GradientFillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/gradient-fill/gradient-fill-converter';
import { IBorderInfo } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { colorStrToColorDmlA, getEgColorChoiceRgba } from '@src/slide/infrastructure/module/independent/color/transform';
import { canvasToEmu, emuToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import { LineJoin } from '@tencent/alloy-renderer';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { BorderSketchType } from '@src/slide/domain/ui-model/element-modeles/property/border/constants';
import { GraphicInfoConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/graphic-info/graphic-info-converter';
import { LINE_DASH_VER3_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/line-dash';
import { LINE_END_TYPE_VER3_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/line-end-type';
import { LINE_END_WIDTH_TYPE_VER3_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/line-end-width';
import { IGraphicInfo } from '@src/slide/domain/ui-model/element-modeles/property/graphic-info/graphic-info.interface';
import { ICTLineEndProperties, DeltaOf, ICTLineProperties, STLineCap, STLineEndWidth, STCompoundLine,
} from '@tencent/slide-sdk-model';
import { ShapeType } from '@src/slide/infrastructure/module/dependent/shape-info/graphic-type';
import _set from 'lodash/set';
import { CustomGraphicInfoConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/custom-graphic-info/custom-graphic-info-converter';
import { invertMap } from '@src/slide/infrastructure/module/independent/object/map-invert';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import {
    ColorConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/color/color-converter';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { ICTShapeProperties } from '@tencent/slide-sdk-pb-model';

// 草绘的 uri 常量，设置为草绘时必有此字段，值固定，否则导出到 office 后类型会错误
const URI_VALUE = '{C807C97D-BFC1-408E-A445-0C87EB9F89A2}';

/**
 * 边框
 */
export class BorderConverter {
    /**
     * 边框的sdk -> ui 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTLineProperties>>,
        uiData: IBorderInfo,
    ): void {
        // 宽度
        BorderConverter.convertLineWidthFromSdkToUi(sdkData, uiData);
        // 线端类型
        BorderConverter.convertLineCapFromSdkToUi(sdkData, uiData);
        // 短划线类型
        BorderConverter.convertLineDashFromSdkToUi(sdkData, uiData);
        // 连接类型
        BorderConverter.convertLineJoinFromSdkToUi(sdkData, uiData);
        // 边框填充类型
        BorderConverter.convertLineFillFromSdkToUi(sdkData, uiData);
        // 头尾类型
        BorderConverter.convertLineEndFromSdkToUi(sdkData, uiData);
        // 草绘
        BorderConverter.convertLineSketchFromSdkToUi(sdkData, uiData);
        // 复合类型
        BorderConverter.convertLineCompoundTypeFromSdkToUi(sdkData, uiData);
    }

    /**
     * 边框的ui-> sdk 映射
     * @param uiData
     * @param sdkData
     * @param rawUiData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IBorderInfo>>,
        sdkData: DeltaOf<ICTLineProperties>,
        rawUiData?: IElementBaseModel,
    ): void {
        // 宽度
        BorderConverter.convertLineWidthFromUiToSdk(uiData, sdkData);
        // 线端类型
        BorderConverter.convertLineCapFromUiToSdk(uiData, sdkData);
        // 短划线类型
        BorderConverter.convertLineDashFromUiToSdk(uiData, sdkData);
        // 连接类型
        BorderConverter.convertLineJoinFromUiToSdk(uiData, sdkData);
        // 边框填充类型
        BorderConverter.convertLineFillFromUiToSdk(uiData, sdkData);
        // 头尾类型
        BorderConverter.convertLineEndFromUiToSdk(uiData, sdkData);
        // 草绘
        BorderConverter.convertLineSketchFromUiToSdk(uiData, sdkData, rawUiData);
        // 复合类型
        BorderConverter.convertLineCompoundTypeFromUiToSdk(uiData, sdkData);
    }

    /**
     * 存在草绘的情况下，controlValues读取草绘节点下的
     * @param sdkData
     * @param uiData
     */
    public static convertControlValuesFromLineSketch(
        sdkData: DeepReadonly<DeepPartial<ICTLineProperties>>,
        uiData: IGraphicInfo,
    ): void {
        const sketch = sdkData.extLst?.ext?.[0]?.lineSketchStyleProps?.[0];
        if (sketch) {
            const graphicInfo = { controlValues: [] };
            if (sketch.egGeometry?.prstGeom) {
                GraphicInfoConverter.convertPartialDataFromSdkToUi(sketch.egGeometry.prstGeom, graphicInfo);
                if (graphicInfo.controlValues?.length) {
                    uiData.controlValues = graphicInfo.controlValues;
                }
            }
        }
    }

    private static convertLineCompoundTypeFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTLineProperties>>,
        uiData: IBorderInfo,
    ): void {
        if (sdkData.cmpd) {
            // @ts-ignore
            uiData.compoundType = STCompoundLine[sdkData.cmpd];
        } else {
            delete uiData.compoundType;
        }
    }

    // 边框宽度
    private static convertLineWidthFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTLineProperties>>,
        uiData: IBorderInfo,
    ): void {
        if (sdkData.w || sdkData.w === 0) {
            uiData.width = emuToCanvas(sdkData.w);
        } else {
            delete uiData.width;
        }
    }

    // 线端类型
    private static convertLineCapFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTLineProperties>>,
        uiData: IBorderInfo,
    ): void {
        if (sdkData.cap) {
            // @ts-ignore
            uiData.linecap = STLineCap[sdkData.cap];
        } else {
            delete uiData.linecap;
        }
    }

    // 短划线类型
    private static convertLineDashFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTLineProperties>>,
        uiData: IBorderInfo,
    ): void {
        if (sdkData.egLineDashProperties?.prstDash?.val) {
            uiData.style = LINE_DASH_VER3_MAP.get(sdkData.egLineDashProperties.prstDash.val);
        } else {
            delete uiData.style;
        }
    }

    // 边框连接类型
    private static convertLineJoinFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTLineProperties>>,
        uiData: IBorderInfo,
    ): void {
        if (sdkData.egLineJoinProperties) {
            if (sdkData.egLineJoinProperties.miter) {
                uiData.linejoin = LineJoin.Miter;
            }
            if (sdkData.egLineJoinProperties.bevel) {
                uiData.linejoin = LineJoin.Bevel;
            }
            if (sdkData.egLineJoinProperties.round) {
                uiData.linejoin = LineJoin.Round;
            }
        } else {
            delete uiData.linejoin;
        }
    }

    // 边框填充类型
    private static convertLineFillFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTLineProperties>>,
        uiData: IBorderInfo,
    ): void {
        if (sdkData.egLineFillProperties) {
            // 无边框
            const noFillData = sdkData.egLineFillProperties.noFill;
            if (noFillData) {
                uiData.noFill = 1;
                delete uiData.color;
                delete uiData.tdColor;
                delete uiData.gradFill;
            } else {
                // 非无填充的情况再处理填充转换，避免歧义
                delete uiData.noFill;

                // 纯色边框
                const egColorChoice = sdkData.egLineFillProperties.solidFill?.egColorChoice;
                if (egColorChoice) {
                    // uiData.color = getEgColorChoiceRgba(solidFillData.egColorChoice?.srgbClr);
                    // uiData.tdColor = new TDColor({
                    //     rgbaStr: uiData.color,
                    // });
                    const tdColor = ColorConverter.convertPartialDataFromSdkToUi(egColorChoice);
                    uiData.tdColor = tdColor;
                    uiData.color = tdColor.srgb_color;
                } else {
                    delete uiData.color;
                }
                // 渐变边框
                const gradFillData = sdkData.egLineFillProperties.gradFill;
                if (gradFillData) {
                    uiData.gradFill = GradientFillConverter.convertPartialDataFromSdkToUi(gradFillData);
                } else {
                    delete uiData.gradFill;
                }
            }
        } else {
            delete uiData.noFill;
            delete uiData.color;
            delete uiData.gradFill;
        }
    }

    // 边框开始结尾箭头
    private static convertLineEndFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTLineProperties>>,
        uiData: IBorderInfo,
    ): void {
        // 开始箭头
        if (sdkData.headEnd?.type) {
            uiData.headEnd = {
                lineEndType: LINE_END_TYPE_VER3_MAP.get(sdkData.headEnd.type)!,
                lengthType: LINE_END_WIDTH_TYPE_VER3_MAP
                    .get(sdkData.headEnd.len as unknown as STLineEndWidth ?? STLineEndWidth.med),
                widthType: LINE_END_WIDTH_TYPE_VER3_MAP.get(sdkData.headEnd.w ?? STLineEndWidth.med),
            };
        } else {
            delete uiData.headEnd;
        }
        // 结尾箭头
        if (sdkData.tailEnd?.type) {
            uiData.tailEnd = {
                lineEndType: LINE_END_TYPE_VER3_MAP.get(sdkData.tailEnd.type)!,
                lengthType: LINE_END_WIDTH_TYPE_VER3_MAP
                    .get(sdkData.tailEnd.len as unknown as STLineEndWidth ?? STLineEndWidth.med),
                widthType: LINE_END_WIDTH_TYPE_VER3_MAP.get(sdkData.tailEnd.w ?? STLineEndWidth.med),
            };
        } else {
            delete uiData.tailEnd;
        }
    }

    // 草绘
    private static convertLineSketchFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTLineProperties>>,
        uiData: IBorderInfo,
    ): void {
        const sketch = sdkData.extLst?.ext?.[0]?.lineSketchStyleProps?.[0];
        if (sketch) {
            uiData.sketchStyle = {};
            const type = Object.keys(sketch.type?.egLineSketchType || {})
                .filter(p => sketch.type?.egLineSketchType?.[p])[0];
            if (type) {
                uiData.sketchStyle.type = type as BorderSketchType;
            }
            const graphicInfo = {};
            if (sketch.egGeometry?.prstGeom) {
                GraphicInfoConverter.convertPartialDataFromSdkToUi(sketch.egGeometry.prstGeom, graphicInfo);
            }
            if (sketch.egGeometry?.custGeom) {
                GraphicInfoConverter.convertPartialDataFromSdkToUi(sketch.egGeometry.custGeom, graphicInfo);
                CustomGraphicInfoConverter.convertPartialDataFromSdkToUi(sketch.egGeometry.custGeom, graphicInfo);
            }
            uiData.sketchStyle = Object.assign(uiData.sketchStyle, graphicInfo);
        } else {
            delete uiData.sketchStyle;
        }
    }

    private static convertLineCompoundTypeFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IBorderInfo>>,
        sdkData: DeltaOf<ICTLineProperties>,
    ): void {
        if (uiData.compoundType) {
            sdkData.cmpd = STCompoundLine[uiData.compoundType];
        }
    }

    // 边框宽度
    private static convertLineWidthFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IBorderInfo>>,
        sdkData: DeltaOf<ICTLineProperties>,
    ): void {
        if (uiData.width || uiData.width === 0) {
            sdkData.w = canvasToEmu(uiData.width);
        } else if (uiData.width === null) {
            sdkData.w = null;
        }
    }

    // 线端类型
    private static convertLineCapFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IBorderInfo>>,
        sdkData: DeltaOf<ICTLineProperties>,
    ): void {
        if (uiData.linecap) {
            sdkData.cap = STLineCap[uiData.linecap] as unknown as STLineCap;
        } else if (uiData.linecap === null) {
            sdkData.cap = null;
        }
    }

    // 短划线类型
    private static convertLineDashFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IBorderInfo>>,
        sdkData: DeltaOf<ICTLineProperties>,
    ): void {
        if (uiData.style) {
            const val = invertMap(LINE_DASH_VER3_MAP).get(uiData.style);
            _set(sdkData, 'egLineDashProperties.prstDash.val', val);
        } else if (uiData.style === null) {
            sdkData.egLineDashProperties = null;
        }
    }

    // 连接类型
    private static convertLineJoinFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IBorderInfo>>,
        sdkData: DeltaOf<ICTLineProperties>,
    ): void {
        if (uiData.linejoin) {
            // 斜角
            if (uiData.linejoin === LineJoin.Miter) {
                _set(sdkData, 'egLineJoinProperties.miter.lim', 800000);
            }
            // 棱台
            if (uiData.linejoin === LineJoin.Bevel) {
                _set(sdkData, 'egLineJoinProperties.bevel', true);
            }
            // 圆角
            if (uiData.linejoin === LineJoin.Round) {
                _set(sdkData, 'egLineJoinProperties.round', true);
            }
        } else if (uiData.linejoin === null) {
            sdkData.egLineJoinProperties = null;
        }
    }

    // 边框填充类型
    private static convertLineFillFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IBorderInfo>>,
        sdkData: DeltaOf<ICTLineProperties>,
    ): void {
        // 设置边框无填充
        if (uiData.noFill === 1) {
            _set(sdkData, 'egLineFillProperties.noFill', true);
            _set(sdkData, 'egLineFillProperties.solidFill', null);
            _set(sdkData, 'egLineFillProperties.gradFill', null);
        } else {
            // 非无填充的情况，按纯色、渐变分别判断
            if (uiData.noFill === null) {
                _set(sdkData, 'egLineFillProperties.noFill', null);
            }

            // 纯色边框
            if (uiData.tdColor) {
                // _set(sdkData, 'egLineFillProperties.solidFill.egColorChoice', colorStrToColorDmlA(uiData.color));
                const egColorChoice = {};
                ColorConverter.convertPartialDataFromUiToSdk(uiData.tdColor, egColorChoice);
                _set(sdkData, 'egLineFillProperties.solidFill.egColorChoice', egColorChoice);
                _set(sdkData, 'egLineFillProperties.noFill', null);
                _set(sdkData, 'egLineFillProperties.gradFill', null);
            } else if (uiData.color === null) {
                _set(sdkData, 'egLineFillProperties.solidFill', null);
            }
            // 渐变边框
            if (uiData.gradFill) {
                _set(sdkData, 'egLineFillProperties.gradFill', GradientFillConverter.convertPartialDataFromUiToSdk(uiData.gradFill));
                _set(sdkData, 'egLineFillProperties.noFill', null);
                _set(sdkData, 'egLineFillProperties.solidFill', null);
            } else if (uiData.gradFill === null) {
                _set(sdkData, 'egLineFillProperties.gradFill', null);
            }
        }
    }

    // 头尾类型
    // eslint-disable-next-line complexity
    private static convertLineEndFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IBorderInfo>>,
        sdkData: DeltaOf<ICTLineProperties>,
    ): void {
        const INVERT_LINE_END_TYPE_VER3_MAP = invertMap(LINE_END_TYPE_VER3_MAP);
        const INVERT_LINE_END_WIDTH_TYPE_VER3_MAP = invertMap(LINE_END_WIDTH_TYPE_VER3_MAP);
        // 开始箭头
        if (uiData.headEnd && Object.keys(uiData.headEnd).length) {
            const { lineEndType, lengthType, widthType } = uiData.headEnd;
            const headEnd: ICTLineEndProperties = {};
            if (lineEndType) {
                headEnd.type = INVERT_LINE_END_TYPE_VER3_MAP.get(lineEndType);
            }
            if (lengthType) {
                headEnd.len = INVERT_LINE_END_WIDTH_TYPE_VER3_MAP.get(lengthType);
            }
            if (widthType) {
                headEnd.w = INVERT_LINE_END_WIDTH_TYPE_VER3_MAP.get(widthType);
            }
            sdkData.headEnd = headEnd;
        } else if (uiData.headEnd === null) {
            sdkData.headEnd = null;
        }
        // 结尾箭头
        if (uiData.tailEnd && Object.keys(uiData.tailEnd).length) {
            const { lineEndType, lengthType, widthType } = uiData.tailEnd;
            const tailEnd: ICTLineEndProperties = {};
            if (lineEndType) {
                tailEnd.type = INVERT_LINE_END_TYPE_VER3_MAP.get(lineEndType);
            }
            if (lengthType) {
                tailEnd.len = INVERT_LINE_END_WIDTH_TYPE_VER3_MAP.get(lengthType);
            }
            if (widthType) {
                tailEnd.w = INVERT_LINE_END_WIDTH_TYPE_VER3_MAP.get(widthType);
            }
            sdkData.tailEnd = tailEnd;
        } else if (uiData.tailEnd === null) {
            sdkData.tailEnd = null;
        }
    }

    // 草绘
    private static convertLineSketchFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IBorderInfo>>,
        sdkData: DeltaOf<ICTLineProperties>,
        rawUiData?: IElementBaseModel,
    ): void {
        if (uiData.sketchStyle) {
            if (uiData.sketchStyle.type) {
                const obj = {};
                Object.values(BorderSketchType).forEach((key) => {
                    if (obj[key]) {
                        obj[key] = null;
                    }
                });
                obj[uiData.sketchStyle.type] = {};
                _set(sdkData, 'extLst.ext[0].lineSketchStyleProps[0].type.egLineSketchType', obj);
            }
            const uri = (rawUiData?.sdkData?.props?.spPr as ICTShapeProperties)?.ln?.extLst?.ext?.[0]?.uri;
            // 有uri则保留，没有则用一个固定值
            _set(sdkData, 'extLst.ext[0].uri', uri || URI_VALUE);
            if (uiData.sketchStyle.bgShape === ShapeType.custom) {
                const custGeom = GraphicInfoConverter.convertPartialDataFromUiToSdk(uiData.sketchStyle as IGraphicInfo);
                CustomGraphicInfoConverter
                    .convertPartialDataFromUiToSdk(uiData.sketchStyle.customGeom as IGraphicInfo, custGeom);
                _set(sdkData, 'extLst.ext[0].lineSketchStyleProps[0].egGeometry.custGeom', custGeom);
            } else {
                const prstGeom = GraphicInfoConverter.convertPartialDataFromUiToSdk(uiData.sketchStyle as IGraphicInfo);
                _set(sdkData, 'extLst.ext[0].lineSketchStyleProps[0].egGeometry.prstGeom', prstGeom);
            }
        }
    }
}
